import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { getToken, logout } from '../lib/auth';
import logo from '../assets/logo.png';

const FEATURES = [
  'Unlimited customer rewards & digital stamp cards',
  'QR code check-ins and printable branding',
  'Targeted campaigns & customer notifications',
  'Insights dashboard and collaborations',
];

function formatPrice(price) {
  if (!price) return '$49.99';
  const amount = (price.amount / 100).toFixed(2);
  const symbol = price.currency === 'aud' ? '$' : '';
  return `${symbol}${amount}`;
}

export default function Subscribe() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = getToken();

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [price, setPrice] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const goToDashboard = useCallback(() => navigate(`/dashboard/${userId}`, { replace: true }), [navigate, userId]);

  // On mount: if returning from Checkout, verify; otherwise load current status.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = params.get('status');
      const sessionId = params.get('session_id');
      try {
        if (status === 'success' && sessionId) {
          const res = await fetch(`/api/vendor/${userId}/subscription/verify`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          if (!cancelled) {
            if (data.ok && data.active) { goToDashboard(); return; }
            setInfo('Your payment is being processed. This can take a moment — please refresh, or contact support if it persists.');
          }
        } else {
          if (status === 'cancel') setInfo('Payment was not completed. Subscribe below to activate your account.');
          const res = await fetch(`/api/vendor/${userId}/subscription`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await res.json();
          if (!cancelled) {
            if (data.ok && data.active) { goToDashboard(); return; }
            if (data.ok && data.price) setPrice(data.price);
          }
        }
      } catch {
        if (!cancelled) setError('Something went wrong. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSubscribe = async () => {
    setWorking(true);
    setError('');
    try {
      const res = await fetch(`/api/vendor/${userId}/subscription/checkout`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.alreadyActive) { goToDashboard(); return; }
      if (!data.ok || !data.url) throw new Error(data.message || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setWorking(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
      <Box component="img" src={logo} alt="Rewarding" sx={{ height: 34, mb: 4 }} />
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 460, p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardGiftcardIcon sx={{ color: '#ffb31b' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e' }}>Activate your membership</Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>One step left to start using Rewarding</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: 3 }}>
          <Typography sx={{ fontSize: 40, fontWeight: 800, color: '#1a1a2e' }}>{formatPrice(price)}</Typography>
          <Typography sx={{ color: '#999', fontWeight: 600 }}>AUD / month</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          {FEATURES.map((f) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#444' }}>{f}</Typography>
            </Box>
          ))}
        </Box>

        {info && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>{info}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubscribe}
          disabled={working}
          sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, minHeight: 48, borderRadius: 2.5, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}
        >
          {working ? 'Redirecting to secure checkout…' : 'Subscribe & activate'}
        </Button>
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#aaa', mt: 1.5 }}>
          Secure payment via Stripe. Cancel anytime from your settings.
        </Typography>

        <Button fullWidth onClick={handleLogout} sx={{ textTransform: 'none', color: '#999', mt: 1 }}>
          Log out
        </Button>
      </Paper>
    </Box>
  );
}
